import React, { useEffect, useState } from "react";
import {
    Dialog,
    useMediaQuery,
    useTheme,
    TextField,
    Switch,
    FormControlLabel,
    Button,
    CircularProgress,
    Typography,
    Box,
    Divider
} from "@mui/material";
import { IoCloseCircleOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Notify } from "../CommonPopup/DashboardPopUp";
import { updateCourse, getAllCourses, openEditCourseDialog } from "../Redux/Dashboard/DashboardSlice";

const EditCourse = () => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const dispatch = useDispatch();
    const { register, handleSubmit, reset, setValue } = useForm();
    const loader = useSelector((s) => s.DashboardSlice.loader);
    const selectedCourse = useSelector((s) => s.DashboardSlice.selectedCourseFolder?.course);
    const open = useSelector((s) => s.DashboardSlice.showEditCourseDialog);

    const [isFree, setIsFree] = useState(true);

    useEffect(() => {
        if (selectedCourse) {
            setValue("title", selectedCourse.title || "");
            setValue("description", selectedCourse.description || "");
            setValue("type", selectedCourse.type || "Free");
            setValue("price", selectedCourse.price || 0);
            setValue("duration", selectedCourse.duration || "");
            // Toggle logic based on is_paid
            const isPaid = selectedCourse?.is_paid;
            setIsFree(!isPaid); // if is_paid is false, then it's free
        }
    }, [selectedCourse, setValue]);

    const handleClose = () => {
        dispatch(openEditCourseDialog(false));
        reset();
    };

    const onSubmit = async (data) => {
        const courseId = selectedCourse?._id;
        if (!courseId) {
            Notify({ data: "error", msg: "Course ID missing", type: "error" });
            return;
        }

        const formData = new FormData();
        formData.append("title", data.title);
        formData.append("description", data.description);
        formData.append("type", isFree ? "Free" : "Paid");
        formData.append("price", isFree ? 0 : data.price);
        formData.append("is_paid", isFree ? false : true);

        if (!isFree) {
            formData.append("duration", data.duration);
        }

        formData.append("content", JSON.stringify(selectedCourse?.content || []));

        try {
            await dispatch(updateCourse({ courseId, FormData: formData, data })).unwrap();
            Notify({ data: "success", msg: data.message || "Course updated successfully" });
            handleClose();
            dispatch(getAllCourses({ isPaid: "" }));
        } catch (error) {
            Notify({ data: "error", msg: error.message || "Update failed", type: "error" });
        }
    };

    return (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 3,
                    bgcolor: "#fafafa",
                },
            }}
        >
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" fontWeight="bold">
                        ✏️ Edit Course Info
                    </Typography>
                    <IoCloseCircleOutline
                        size={28}
                        onClick={handleClose}
                        style={{ cursor: "pointer", color: "#f44336" }}
                    />
                </Box>

                <Divider sx={{ mb: 3 }} />

                <form onSubmit={handleSubmit(onSubmit)}>
                    <TextField
                        label="Course Title"
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        {...register("title")}
                        required
                    />

                    <TextField
                        label="Course Description"
                        fullWidth
                        multiline
                        rows={5}
                        variant="outlined"
                        margin="normal"
                        {...register("description")}
                        required
                    />

                    <Box mt={2} mb={2}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isFree}
                                    onChange={() => setIsFree(!isFree)}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="subtitle1">
                                    {isFree ? "Free Course" : "Paid Course"}
                                </Typography>
                            }
                        />
                    </Box>

                    {!isFree && (
                        <>
                            <TextField
                                label="Price (INR)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                {...register("price")}
                                required
                            />
                            <TextField
                                label="Duration (in hours)"
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                {...register("duration")}
                                required
                            />
                        </>
                    )}

                    <Box mt={4} textAlign="center">
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            type="submit"
                            sx={{ px: 4, py: 1.5, borderRadius: 2 }}
                            disabled={loader}
                        >
                            {loader ? <CircularProgress size={24} /> : "Update Course"}
                        </Button>
                    </Box>
                </form>
            </Box>
        </Dialog>
    );
};

export default EditCourse;
